import React, { useEffect, useRef, useState } from "react";
import DailyIframe from "@daily-co/daily-js";
import { X, MessageSquare, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function VideoCallModal({ call, onClose }) {
  const { user } = useAuth();
  const containerRef = useRef(null);
  const frameRef = useRef(null);
  const chatBottomRef = useRef(null);
  const [showChat, setShowChat] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [text, setText] = useState("");

  useEffect(() => {
    if (!call) return;
    let frame;
    try {
      const existing = DailyIframe.getCallInstance?.();
      if (existing) existing.destroy();
    } catch {}
    try {
      frame = DailyIframe.createFrame(containerRef.current, {
        showLeaveButton: true,
        showFullscreenButton: true,
        iframeStyle: { width: "100%", height: "100%", border: "0", borderRadius: "16px" },
      });
      frameRef.current = frame;
      frame.join({ url: call.roomUrl, token: call.token });
      frame.on("left-meeting", () => onClose());
      frame.on("app-message", (ev) => {
        const d = ev?.data;
        if (d?.kind === "xealth-chat") {
          setMessages((m) => [...m, { id: Date.now() + Math.random(), name: d.name, text: d.text, mine: false }]);
          setShowChat((open) => { if (!open) setUnread((u) => u + 1); return open; });
        }
      });
    } catch (e) {
      console.error("Daily frame error", e);
      onClose();
    }
    return () => {
      try { frame?.destroy(); } catch {}
      frameRef.current = null;
    };
  }, [call, onClose]);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, showChat]);

  const sendChat = (e) => {
    e.preventDefault();
    if (!text.trim() || !frameRef.current) return;
    const payload = { kind: "xealth-chat", name: user?.name || "Me", text };
    try { frameRef.current.sendAppMessage(payload, "*"); } catch {}
    setMessages((m) => [...m, { id: Date.now() + Math.random(), name: "You", text, mine: true }]);
    setText("");
  };

  if (!call) return null;

  return (
    <div data-testid="video-call-modal" className="fixed inset-0 z-[120] bg-black/95 flex items-center justify-center p-3 sm:p-6">
      <div className="relative w-full max-w-6xl h-[82vh] flex gap-3">
        <div className="relative flex-1">
          <div ref={containerRef} className="w-full h-full rounded-2xl overflow-hidden bg-[#140A24]" />
          <button
            data-testid="toggle-call-chat-btn"
            onClick={() => { setShowChat((s) => !s); setUnread(0); }}
            className="absolute bottom-4 left-4 w-11 h-11 rounded-full bg-[#6C0BA9] hover:bg-[#8012C6] text-white flex items-center justify-center shadow-lg"
            title="In-call chat"
          >
            <MessageSquare className="w-5 h-5" />
            {unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E024A3] text-[10px] font-bold flex items-center justify-center">{unread}</span>}
          </button>
          <button
            data-testid="end-call-btn"
            onClick={() => { try { frameRef.current?.destroy(); } catch {}; onClose(); }}
            className="absolute -top-2 -right-2 w-10 h-10 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg z-10"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {showChat && (
          <div data-testid="call-chat-panel" className="w-72 shrink-0 bg-card border border-border rounded-2xl flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-border font-heading font-bold text-sm flex items-center justify-between">
              In-call chat
              <button onClick={() => setShowChat(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {messages.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center mt-4">Say hi to everyone in the call 👋</p>
              ) : messages.map((m) => (
                <div key={m.id} className={`flex flex-col ${m.mine ? "items-end" : "items-start"}`}>
                  <span className="text-[10px] text-muted-foreground px-1">{m.name}</span>
                  <div className={`max-w-[85%] px-3 py-1.5 rounded-2xl text-sm ${m.mine ? "bg-[#6C0BA9] text-white rounded-br-sm" : "bg-secondary rounded-bl-sm"}`}>{m.text}</div>
                </div>
              ))}
              <div ref={chatBottomRef} />
            </div>
            <form onSubmit={sendChat} className="p-2.5 border-t border-border flex gap-2">
              <input data-testid="call-chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Message..." className="flex-1 bg-input border border-border rounded-full px-3 h-9 text-sm outline-none focus:border-[#6C0BA9]" />
              <button data-testid="call-chat-send-btn" type="submit" className="w-9 h-9 shrink-0 rounded-full bg-[#6C0BA9] hover:bg-[#8012C6] text-white flex items-center justify-center"><Send className="w-4 h-4" /></button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
